import {
  CircularProgress,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
} from "@chakra-ui/react";

import React from "react";
import { Center, Stack, Text } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import theme from "../../theme/theme";
import { useStores } from "../../hooks/useStores";
import { Project } from "../../types";
import { DetailsTable } from "./../projects/summary/details/DetailsTable";
import {
  getInvestmentPerPhasePlotValues,
  getLCCInvestmentPerPhasePlotValues,
} from "../../services/utils";
import Plot from "react-plotly.js";
import { BenchmarkPlotsTab } from "../projects/summary/report/BenchmarkPlotsTab";

type Props = {
  projectId: number;
  isBenchmark: boolean;
};

export const GeneralPlots: React.FC<Props> = observer((props) => {
  const { t } = useTranslation();
  const { ui, projects } = useStores();
  ui.setActiveScreen("projects");

  const investmentPerPhasePlotTitle = t("plotTitles.investmentPerPhase");
  const investmentPerPhasePlotLabels = [
    t("tablesVoices.report.priceForPhase.project"),
    t("tablesVoices.report.priceForPhase.fancadeProduction"),
    t("tablesVoices.report.priceForPhase.transportToConstructionSite"),
    t("tablesVoices.report.priceForPhase.installation"),
  ];

  const investmentPerPhasePlotColors = [
    theme.colors.projectMarker,
    theme.colors.productionMarker,
    theme.colors.transportMarker,
    theme.colors.installationMarker,
  ];

  const lccInvestmentPerPhasePlotTitle = t("plotTitles.lccInvestmentPerPhase");
  const lccInvestmentPerPhasePlotLabels = [
    t("tablesVoices.report.priceForPhase.project"),
    t("tablesVoices.report.priceForPhase.fancadeProduction"),
    t("tablesVoices.report.priceForPhase.transportToConstructionSite"),
    t("tablesVoices.report.priceForPhase.installation"),

    t("tablesVoices.report.priceForPhase.serviceLife"),
    t("tablesVoices.report.priceForPhase.maintenance"),
    t("tablesVoices.report.priceForPhase.disposal"),
    t("tablesVoices.report.priceForPhase.recycling"),
  ];
  const lccInvestmentPerPhasePlotColors = [
    theme.colors.projectMarker,
    theme.colors.productionMarker,
    theme.colors.transportMarker,
    theme.colors.installationMarker,
    theme.colors.serviceLifeMarker,
    theme.colors.maintenanceMarker,
    theme.colors.disposalMarker,
    theme.colors.recyclingMarker,
  ];

  const [tabIndex, setTabIndex] = React.useState(0);

  if (projects.isFetchingProjects) {
    return (
      <Center height={"100%"}>
        <CircularProgress
          size={"2em"}
          color={theme.colors.euracOrange}
          isIndeterminate
        />
      </Center>
    );
  }

  //Find the current project
  const currentProject: Project | undefined = projects.projects.find(
    (project) => project.id == props.projectId
  );

  if (currentProject == undefined)
    return <Center>{t("errors.generic")}</Center>;
  const handleTabsChange = (index: number) => {
    setTabIndex(index);
  };
  return (
    <Stack w={"99%"} h={"85vh"} overflowY={"scroll"}>
      <Tabs
        index={tabIndex}
        onChange={handleTabsChange}
        variant={"enclosed"}
        w={"100%"}
        paddingTop={"0.2rem"}
        paddingLeft={"0.1rem"}
      >
        <TabList>
          {currentProject?.fancades?.map((fancade, index) => (
            <Tab alignItems={"center"}>
              <Text color={theme.colors.darkGrey2}>
                {fancade.customName
                  ? currentProject.isBenchmark && index == 0
                    ? t("tabs.details.fancadeTechnicalProject.benchmark") +
                      fancade.customName
                    : fancade.customName
                  : currentProject.isBenchmark && index == 0
                  ? t("tabs.details.fancadeTechnicalProject.firstTab")
                  : t("tabs.details.fancadeTechnicalProject.tabsAfterFirst") +
                    (index + 1)}
              </Text>
            </Tab>
          ))}
          {!currentProject.isBenchmark &&
            currentProject.fancades &&
            currentProject.fancades.length > 0 && (
              <Tab alignItems={"center"}>
                <Text color={theme.colors.darkGrey2}>
                  {t("tablesVoices.report.allFancadesTab")}
                </Text>
              </Tab>
            )}
          {currentProject.isBenchmark &&
            currentProject.fancades &&
            currentProject.fancades.length > 1 && (
              <Tab alignItems={"center"}>
                <Text color={theme.colors.darkGrey2}>
                  {t("tablesVoices.report.benchmarkTab")}
                </Text>
              </Tab>
            )}
        </TabList>
        <TabPanels>
          {currentProject?.fancades?.map((fancade, index) => (
            <TabPanel w={"100%"}>
              {getInvestmentPerPhasePlotValues(currentProject, index).length >
              0 ? (
                <Plot
                  data={[
                    {
                      values: getInvestmentPerPhasePlotValues(
                        currentProject,
                        index
                      ),
                      labels: investmentPerPhasePlotLabels,
                      type: "pie",
                      marker: {
                        colors: investmentPerPhasePlotColors,
                      },
                    },
                  ]}
                  layout={{
                    title: investmentPerPhasePlotTitle,
                  }}
                />
              ) : (
                <Text textAlign={"center"} fontSize={"2xl"}>
                  {t("errors.noValuesPlot")}
                </Text>
              )}
              {getLCCInvestmentPerPhasePlotValues(currentProject, index)
                .length > 0 ? (
                <Plot
                  data={[
                    {
                      values: getLCCInvestmentPerPhasePlotValues(
                        currentProject,
                        index
                      ),
                      labels: lccInvestmentPerPhasePlotLabels,
                      type: "pie",
                      marker: {
                        colors: lccInvestmentPerPhasePlotColors,
                      },
                    },
                  ]}
                  layout={{
                    title: lccInvestmentPerPhasePlotTitle,
                    margin: {
                      t: 200,
                    },
                  }}
                />
              ) : (
                <Text textAlign={"center"} fontSize={"2xl"}>
                  {t("errors.noValuesPlot")}
                </Text>
              )}
            </TabPanel>
          ))}
          {/* {currentProject.fancades && currentProject.fancades.length > 0 && (
            <TabPanel w={"100%"} overflowX={"scroll"}>
              {getInvestmentPerPhasePlotValues(
                currentProject,
                -1,
                props.isBenchmark
              ).length > 0 ? (
                <Plot
                  data={[
                    {
                      values: getInvestmentPerPhasePlotValues(
                        currentProject,
                        -1,
                        props.isBenchmark
                      ),
                      labels: investmentPerPhasePlotLabels,
                      type: "pie",
                      marker: {
                        colors: investmentPerPhasePlotColors,
                      },
                    },
                  ]}
                  layout={{
                    title: investmentPerPhasePlotTitle,
                  }}
                />
              ) : (
                <Text textAlign={"center"} fontSize={"2xl"}>
                  {t("errors.noValuesPlot")}
                </Text>
              )}

              {getLCCInvestmentPerPhasePlotValues(
                currentProject,
                -1,
                props.isBenchmark
              ).length > 0 ? (
                <Plot
                  data={[
                    {
                      values: getLCCInvestmentPerPhasePlotValues(
                        currentProject,
                        -1,
                        props.isBenchmark
                      ),
                      labels: lccInvestmentPerPhasePlotLabels,
                      type: "pie",
                      marker: {
                        colors: lccInvestmentPerPhasePlotColors,
                      },
                    },
                  ]}
                  layout={{
                    title: lccInvestmentPerPhasePlotTitle,
                    margin: {
                      t: 200,
                    },
                  }}
                />
              ) : (
                <Text textAlign={"center"} fontSize={"2xl"}>
                  {t("errors.noValuesPlot")}
                </Text>
              )}
              {getLCCInvestmentPerPhasePlotValues(currentProject, 0).length >
                0 && (
                <Plot
                  style={{ paddingBottom: "3rem" }}
                  data={getLCCInvestmentPerPhasePlotValues(
                    currentProject,
                    0
                  ).map((value, valueIndex) => {
                    let values: any[] = [];
                    let x: any[] = [];
                    if (currentProject && currentProject.fancades) {
                      values = currentProject.fancades.map(
                        (fancade, fancadeIndex) => {
                          if (fancadeIndex === 0 && props.isBenchmark)
                            x.push("Benchmark");
                          else
                            x.push(t("fancade") + " #" + (+fancadeIndex + 1));
                          return getLCCInvestmentPerPhasePlotValues(
                            currentProject,
                            fancadeIndex
                          )[valueIndex];
                        }
                      );
                    }
                    return {
                      y: values,
                      x: x,
                      type: "bar",
                      name: lccInvestmentPerPhasePlotLabels[valueIndex],
                      marker: {
                        color: lccInvestmentPerPhasePlotColors[valueIndex],
                      },
                    };
                  })}
                  layout={{
                    title: lccInvestmentPerPhasePlotTitle,
                    barmode: "stack",
                    xaxis: { automargin: true },
                    height: 500,
                  }}
                />
              )}
            </TabPanel>
          )} */}
          {currentProject.isBenchmark &&
            currentProject.fancades &&
            currentProject.fancades.length > 1 && (
              <TabPanel w={"100%"}>
                <BenchmarkPlotsTab project={currentProject} />
              </TabPanel>
            )}
        </TabPanels>
      </Tabs>
      {!currentProject.fancades ||
        (currentProject?.fancades?.length == 0 && (
          <DetailsTable columnsTitles={[]} rows={[]} />
        ))}
    </Stack>
  );
});
